'use client';

import { useEffect, useMemo, useState } from 'react';
import { Lock, Unlock, Image, Video, Music, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { BotMediaReference } from '@/types/bot';
import { useContentUnlock } from '@/hooks/use-content-unlock';
import { useEntitlements } from '@/hooks/use-entitlements';
import { ImageLightbox } from '@/components/chat/ImageLightbox/ImageLightbox';
import { useSession } from '@/hooks/useSessionHook';
import supabase from '@/supabase';

interface MediaReferenceCardProps {
  mediaReference: BotMediaReference;
  onUnlockSuccess?: () => void;
}

const mediaTypeIcons = {
  image: Image,
  video: Video,
  audio: Music,
};

const COMPANION_ACCESS_RANK = {
  free: 0,
  preferred: 1,
  private: 2,
} as const;

function normalizeMembershipTier(value: unknown): 'free' | 'preferred' | 'private' {
  if (value === 'preferred' || value === 'private') {
    return value;
  }
  return 'free';
}

export function MediaReferenceCard({
  mediaReference,
  onUnlockSuccess,
}: MediaReferenceCardProps) {
  const { session } = useSession();
  const [membershipTier, setMembershipTier] = useState<'free' | 'preferred' | 'private'>('free');
  const [isUnlocked, setIsUnlocked] = useState(mediaReference.access_tier_required === 'free');
  const [showLightbox, setShowLightbox] = useState(false);
  const { unlockContent, checkIfUnlocked, isUnlocking, error, clearError } = useContentUnlock();
  const { credits } = useEntitlements();

  const MediaIcon = mediaTypeIcons[mediaReference.type];
  const canAfford = credits >= mediaReference.credits_required;
  const hasMembershipAccess = useMemo(
    () => COMPANION_ACCESS_RANK[membershipTier] >= COMPANION_ACCESS_RANK[mediaReference.access_tier_required],
    [membershipTier, mediaReference.access_tier_required]
  );

  useEffect(() => {
    let cancelled = false;

    const loadAccess = async () => {
      if (!session?.user?.id) {
        setMembershipTier('free');
        setIsUnlocked(mediaReference.access_tier_required === 'free');
        return;
      }

      const { data: membership } = await supabase
        .from('bot_memberships')
        .select('access_tier')
        .eq('bot_id', mediaReference.bot_id)
        .eq('user_id', session.user.id)
        .maybeSingle();

      const nextTier = normalizeMembershipTier(membership?.access_tier);

      if (cancelled) {
        return;
      }

      setMembershipTier(nextTier);

      const membershipUnlocks = COMPANION_ACCESS_RANK[nextTier] >= COMPANION_ACCESS_RANK[mediaReference.access_tier_required];
      if (membershipUnlocks || mediaReference.access_tier_required === 'free') {
        setIsUnlocked(true);
        return;
      }

      const alreadyUnlocked = await checkIfUnlocked(mediaReference.id, 'media');
      if (!cancelled) {
        setIsUnlocked(alreadyUnlocked);
      }
    };

    void loadAccess();

    return () => {
      cancelled = true;
    };
  }, [checkIfUnlocked, mediaReference.access_tier_required, mediaReference.bot_id, mediaReference.id, session?.user?.id]);

  const handleUnlock = async () => {
    clearError();
    const result = await unlockContent(
      mediaReference.id,
      'media',
      mediaReference.credits_required
    );
    if (result?.success) {
      setIsUnlocked(true);
      onUnlockSuccess?.();
    }
  };

  const handleMediaClick = () => {
    if (isUnlocked && mediaReference.type === 'image') {
      setShowLightbox(true);
    }
  };

  const displayUrl = isUnlocked ? mediaReference.media_url : mediaReference.preview_url;

  return (
    <>
      <div
        data-testid={`media-reference-${mediaReference.id}`}
        className="jr-card mt-2 max-w-xs overflow-hidden rounded-[20px] border-[color:var(--jr-border)] py-0"
      >
        {/* Media preview/content */}
        <div
          className={cn(
            'relative aspect-video bg-muted cursor-pointer',
            !isUnlocked && 'overflow-hidden'
          )}
          onClick={handleMediaClick}
        >
          {mediaReference.type === 'image' && (
            <img
              src={displayUrl}
              alt={mediaReference.title || 'Media'}
              className={cn(
                'w-full h-full object-cover transition-all duration-300',
                !isUnlocked && 'blur-lg scale-110'
              )}
            />
          )}

          {mediaReference.type === 'video' && (
            <>
              <img
                src={mediaReference.preview_url}
                alt={mediaReference.title || 'Video thumbnail'}
                className={cn(
                  'w-full h-full object-cover',
                  !isUnlocked && 'blur-lg scale-110'
                )}
              />
              {isUnlocked && (
                <video
                  src={mediaReference.media_url}
                  controls
                  className="absolute inset-0 w-full h-full"
                  poster={mediaReference.preview_url}
                />
              )}
            </>
          )}

          {mediaReference.type === 'audio' && (
            <div className="flex items-center justify-center w-full h-full bg-gradient-to-br from-primary/20 to-primary/5">
              <Music className="w-12 h-12 text-primary/50" />
            </div>
          )}

          {/* Lock overlay */}
          {!isUnlocked && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm">
              <div className="mb-2 rounded-full bg-black/50 p-3">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <span className="text-white text-sm font-medium">
                {mediaReference.credits_required} tokens to unlock
              </span>
            </div>
          )}

          {/* Media type badge */}
          <div className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-xs text-white">
            <MediaIcon className="w-3 h-3" />
            <span className="capitalize">{mediaReference.type}</span>
          </div>

          {/* Unlocked badge */}
          {isUnlocked && !hasMembershipAccess && mediaReference.is_premium && (
            <div className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-[color:rgba(142,160,122,0.8)] px-2 py-1 text-xs text-[#1f1714]">
              <Unlock className="w-3 h-3" />
              <span>Unlocked</span>
            </div>
          )}
        </div>

        {/* Card footer with title and unlock button */}
        <div className="p-3">
          {mediaReference.title && (
            <p className="mb-2 line-clamp-2 text-sm font-medium text-[var(--jr-text)]">
              {mediaReference.title}
            </p>
          )}

          {!isUnlocked && (
            <div className="space-y-2">
              <Button
                onClick={handleUnlock}
                disabled={isUnlocking || !canAfford}
                className="jr-action-primary h-auto w-full min-h-10"
                size="sm"
              >
                {isUnlocking ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Unlocking...
                  </>
                ) : (
                  <>
                    <Unlock className="w-4 h-4" />
                    Unlock for {mediaReference.credits_required} tokens
                  </>
                )}
              </Button>

              {!canAfford && (
                <p className="text-center text-xs text-[var(--jr-text-muted)]">
                  You have {credits} tokens
                </p>
              )}

              {error && (
                <p className="text-center text-xs text-[color:#efc2bd]">{error}</p>
              )}
            </div>
          )}

          {/* Audio player for unlocked audio */}
          {isUnlocked && mediaReference.type === 'audio' && (
            <audio
              src={mediaReference.media_url}
              controls
              className="w-full mt-2"
            />
          )}
        </div>
      </div>

      {/* Lightbox for images */}
      {showLightbox && isUnlocked && mediaReference.type === 'image' && (
        <ImageLightbox
          images={[
            {
              type: 'image',
              url: mediaReference.media_url,
              thumbnailUrl: mediaReference.preview_url,
              width: mediaReference.width,
              height: mediaReference.height,
            },
          ]}
          currentIndex={0}
          onClose={() => setShowLightbox(false)}
          onNext={() => {}}
          onPrev={() => {}}
        />
      )}
    </>
  );
}
